import React, { useState } from "react";
import I18n from 'i18n.js.erb'
import { isDefined } from "../../utils/utils";
import SubmissionListWithdrawalConfirmation from "./SubmissionListWithdrawalConfirmation";
import SubmissionListWithdrawalSuccess from "./SubmissionListWithdrawalSuccess";
import SubmissionListWithdrawalCallbackModal from "./SubmissionListWithdrawalCallbackModal";
import SubmissionListWithdrawalSelect from "./SubmissionListWithdrawalSelect";


// Figma: sull'ordine già arrivato in sede il modulo mostra anche l'indirizzo a cui tornano le
// carte, che è quello dell'ordine: si legge soltanto, si cambia dall'area utente
const ADDRESS_FIELDS = [
  'first_name', 'last_name', 'co', 'country_id', 'province', 'city', 'cap', 'street_square',
  'number', 'indoor', 'phone_number', 'email'
];

// Motivo "problemi con la spedizione o il corriere": di solito lo risolve il customer support
// programmando un altro ritiro, quindi solo qui si propone di essere ricontattati
const PICK_UP_PROBLEM_REASON = 'home_pick_up_not_carried_out';

// Stesso tetto applicato dal server (BackofficeHelper::CANCELLATION_REQUEST_EXPLANATION_MAX_CHARS)
const EXPLANATION_MAX_CHARS = 500;

// Stessi formati validati dal server (CancellationRequest::IBAN_FORMAT / SWIFT_FORMAT),
// controllati dopo aver tolto gli spazi e portato in maiuscolo
const IBAN_FORMAT = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
const SWIFT_FORMAT = /^[A-Z0-9]{8}([A-Z0-9]{3})?$/;

const normalizedBankCode = (value) => value.replace(/\s/g, '').toUpperCase();


const FieldError = ({ show, message }) => {
  if (!show) return null;

  return <p className="slx-form__error">{message || I18n.t('js.submissionListWithdrawalPage.requiredField')}</p>;
};


// Campo compilabile nella stessa pillola etichettata dei dati di sola lettura (Figma 175:662):
// pastiglia grigia col nome del campo, input senza bordo dentro la shell
const TextField = ({ label, value, error, errorMessage, onChange }) => {

  return <div className="slx-form__field">
    <div className={`slx-form__readonly ${error ? 'slx-form__readonly--error' : ''}`}>
      <span className="slx-form__readonly-label">{label}</span>
      <input
        type="text"
        className="slx-form__labeled-input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
    <FieldError show={error} message={errorMessage} />
  </div>
};


const SelectField = ({ value, options, error, onChange }) => {

  return <div className="slx-form__field">
    <SubmissionListWithdrawalSelect
      value={value}
      options={options}
      placeholder={I18n.t('js.submissionListWithdrawalPage.choose')}
      toggleClassName={`slx-form__control ${error ? 'slx-form__control--error' : ''}`}
      onChange={onChange}
    />
    <FieldError show={error} />
  </div>
};


// Spiegazione estesa: sta sotto la tendina, qualunque sia il motivo scelto (Figma 310:1148), ma
// compare solo dopo che un motivo è stato scelto
const TextAreaField = ({ value, placeholder, error, onChange }) => {

  return <div className="slx-form__field">
    <textarea
      className={`slx-form__control slx-form__control--textarea ${error ? 'slx-form__control--error' : ''}`}
      placeholder={placeholder}
      value={value}
      rows={4}
      maxLength={EXPLANATION_MAX_CHARS}
      onChange={(event) => onChange(event.target.value)}
    />
    <FieldError show={error} />
  </div>
};


// Dato di sola lettura: pillola bordata con l'etichetta del campo in testa, come i campi
// compilabili ma senza possibilita' di scrivere (Figma 175:662)
const ReadOnlyField = ({ label, value }) => {
  const filled = isDefined(value);

  return <div className="slx-form__field">
    <div className="slx-form__readonly">
      <span className="slx-form__readonly-label">{label}</span>
      <span className={`slx-form__readonly-value ${filled ? '' : 'slx-form__readonly-value--empty'}`}>
        {filled ? value : I18n.t('js.submissionListWithdrawalPage.emptyValue')}
      </span>
    </div>
  </div>
};


const ReadOnlyAddressField = ({ field, address }) => {

  return <ReadOnlyField
    label={I18n.t(`js.submissionListWithdrawalPage.addressFields.${field}`)}
    value={address[field]}
  />
};


// action: eventuale link affiancato al titolo, allineato a destra (Figma 310:1293)
const FormSection = ({ title, description, action, children }) => {

  return <div className="slx-form__section">
    {
      action ?
        <div className="slx-form__section-header">
          <h2 className="slx-form__section-title">{title}</h2>
          {action}
        </div>
      :
        <h2 className="slx-form__section-title">{title}</h2>
    }
    {description && <p className="slx-form__section-description">{description}</p>}
    {children}
  </div>
};


// Figma: la fascia in testa annuncia il passo corrente, non il modulo nel suo insieme
const PageBanner = ({ title }) => {

  return <div className="slx-page-banner">
    <h1 className="slx-page-banner__title">{title}</h1>
  </div>
};


const initialAddress = (shipmentAddress, orderOwner) => {
  const address = ADDRESS_FIELDS.reduce((acc, field) => ({ ...acc, [field]: shipmentAddress?.[field] || '' }), {});

  return {
    ...address,
    first_name: address.first_name || orderOwner?.first_name || '',
    last_name: address.last_name || orderOwner?.last_name || '',
    email: address.email || orderOwner?.email || ''
  }
};


// withdrawalReasons e refundMethods arrivano dal server come { key, locale }: le liste ammesse
// restano una sola, quella che l'endpoint di recesso usa per validare
const localizedOptions = (options) => options.map(({ key, locale }) => ({ value: key, label: I18n.t(locale) }));


const SubmissionListWithdrawalForm = ({
  originalSubmissionList,
  countries,
  withdrawalReasons,
  refundMethods,
  previousBankDetails,
  callbackTimeSlots,
  callbackNonWorkingDays,
  orderOwner,
  links
}) => {

  const submissionList = JSON.parse(originalSubmissionList);
  // Le carte vanno restituite solo se sono già arrivate in sede: finché il pacco è in viaggio
  // o non è mai partito non c'è niente da rispedire, quindi l'indirizzo di reso non serve
  const cardsReceived = isDefined(submissionList.received_at);

  const [reason, setReason] = useState('');
  const [explanation, setExplanation] = useState('');
  const [refundMethod, setRefundMethod] = useState('');
  // Le coordinate partono da quelle dell'ultimo recesso con bonifico dell'utente: chi ha gia'
  // fatto un reso non le riscrive, e puo' comunque correggerle
  const [accountHolder, setAccountHolder] = useState(previousBankDetails?.account_holder || '');
  const [iban, setIban] = useState(previousBankDetails?.iban || '');
  const [swift, setSwift] = useState(previousBankDetails?.swift || '');
  const address = initialAddress(submissionList.shipment_address, orderOwner);
  const [errors, setErrors] = useState({});
  // Il riepilogo è il secondo passo dello stesso modulo: i dati compilati restano in memoria
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Recesso registrato: l'ordine non è più annullabile, quindi l'esito si mostra qui
  // invece di rimandare alla pagina ordine
  const [withdrawalDone, setWithdrawalDone] = useState(false);
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  // Cambiato motivo, il link del popup resta montato finché non finisce l'animazione d'uscita
  const [callbackLinkLeaving, setCallbackLinkLeaving] = useState(false);
  // Richiesta di ricontatto inviata ({ day, timeSlot }): il cliente ha rinunciato a recedere, quindi
  // al posto del modulo si mostra l'esito e nessun recesso viene registrato
  const [callbackRequest, setCallbackRequest] = useState(null);

  // Bonifico scelto esplicitamente: lo esegue il backoffice a mano, quindi il modulo deve
  // raccogliere le coordinate del cliente. Le voci proposte arrivano gia' filtrate dal server
  // (PayPal compare solo sugli ordini pagati li')
  const bankDetailsRequired = refundMethod === 'bank_transfer';

  const validate = () => {
    const missing = {
      reason: !isDefined(reason),
      // senza motivo la textarea non è a schermo: l'errore le spetta solo quando si vede
      explanation: isDefined(reason) && !isDefined(explanation.trim()),
      refund_method: !isDefined(refundMethod),
      account_holder: bankDetailsRequired && !isDefined(accountHolder.trim()),
      iban: bankDetailsRequired && !IBAN_FORMAT.test(normalizedBankCode(iban)),
      // lo SWIFT resta facoltativo (i bonifici SEPA non lo richiedono), ma se scritto deve tornare
      swift: bankDetailsRequired && isDefined(swift.trim()) && !SWIFT_FORMAT.test(normalizedBankCode(swift))
    };

    setErrors(missing);
    return !Object.values(missing).some(Boolean);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    setShowConfirmation(true);
  };

  const onConfirm = () => {
    if (!termsAccepted) return setTermsError(true);
    if (submitting) return;

    setSubmitting(true);
    setSubmitError('');

    fetch(`/submission_lists/${submissionList.id}/withdrawal`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      },
      body: JSON.stringify({
        // il motivo e' sempre una chiave, 'other' compresa: il testo del cliente viaggia a parte
        cancellation_reason: reason,
        cancellation_explanation: explanation.trim(),
        refund_method: refundMethod,
        ...(bankDetailsRequired ? {
          customer_bank_account_holder: accountHolder.trim(),
          customer_iban: normalizedBankCode(iban),
          customer_swift: normalizedBankCode(swift)
        } : {})
      })
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw Error(data.message);

        setWithdrawalDone(true);
      })
      .catch((error) => {
        console.error('ERROR:', error);
        setSubmitError(error.message || I18n.t('js.submissionListWithdrawalPage.errors.generic'));
        setSubmitting(false);
      });
  };

  const reasonOptions = localizedOptions(withdrawalReasons);
  const refundMethodOptions = localizedOptions(refundMethods);
  const selectedLabel = (options, value) => options.find((option) => option.value === value)?.label;

  // L'esito non ha la fascia in testa: nel Figma la pagina parte direttamente dal titolo
  if (withdrawalDone || callbackRequest)
    return (
      <div className="slx-page">
        <SubmissionListWithdrawalSuccess
          orderNumber={submissionList.number}
          confirmationDate={submissionList.confirmation_date}
          links={links}
          callback={callbackRequest}
        />
      </div>
    );

  if (showConfirmation)
    return (
      <div className="slx-page">
        <PageBanner title={I18n.t('js.submissionListWithdrawalPage.confirmationTitle')} />

        <SubmissionListWithdrawalConfirmation
          reasonLabel={selectedLabel(reasonOptions, reason)}
          explanation={explanation.trim()}
          refundMethodLabel={selectedLabel(refundMethodOptions, refundMethod)}
          bankDetails={bankDetailsRequired ? { accountHolder: accountHolder.trim(), iban: normalizedBankCode(iban), swift: normalizedBankCode(swift) } : null}
          address={address}
          countryName={countries[address.country_id]?.name}
          orderNumber={submissionList.number}
          cardsReceived={cardsReceived}
          termsAccepted={termsAccepted}
          termsError={termsError}
          onTermsChange={(accepted) => { setTermsAccepted(accepted); setTermsError(false) }}
          onBack={() => setShowConfirmation(false)}
          onConfirm={onConfirm}
          submitting={submitting}
          submitError={submitError}
        />
      </div>
    );

  return (
    <div className="slx-page">
      <PageBanner title={I18n.t('js.submissionListWithdrawalPage.title')} />

      <form className="slx-form" onSubmit={onSubmit} noValidate>
        <div className="slx-form__card">
          <div className="slx-form__sections">
            <FormSection
              title={I18n.t('js.submissionListWithdrawalPage.reasonTitle')}
              description={I18n.t('js.submissionListWithdrawalPage.reasonDescription')}
              action={
                (reason === PICK_UP_PROBLEM_REASON || callbackLinkLeaving) &&
                  <button
                    type="button"
                    className={`slx-form__section-action ${callbackLinkLeaving ? 'slx-form__section-action--leaving' : ''}`}
                    tabIndex={callbackLinkLeaving ? -1 : undefined}
                    onClick={() => setShowCallbackModal(true)}
                    onAnimationEnd={() => { if (callbackLinkLeaving) setCallbackLinkLeaving(false) }}
                  >
                    <span className="slx-form__section-action-icon" />
                    {I18n.t('js.submissionListWithdrawalPage.callback.title')}
                  </button>
              }
            >
              <SelectField
                value={reason}
                options={reasonOptions}
                error={errors.reason}
                onChange={(value) => {
                  // l'uscita parte lasciando il motivo del ritiro e si interrompe solo se lo si risceglie
                  if (value === PICK_UP_PROBLEM_REASON) setCallbackLinkLeaving(false);
                  else if (reason === PICK_UP_PROBLEM_REASON) setCallbackLinkLeaving(true);
                  setReason(value);
                  setErrors((previous) => ({ ...previous, reason: false }));
                }}
              />

              {
                isDefined(reason) &&
                  <TextAreaField
                    value={explanation}
                    placeholder={I18n.t('js.submissionListWithdrawalPage.explanationPlaceholder')}
                    error={errors.explanation}
                    onChange={(value) => { setExplanation(value); setErrors((previous) => ({ ...previous, explanation: false })) }}
                  />
              }
            </FormSection>

            <FormSection
              title={I18n.t('js.submissionListWithdrawalPage.refundTitle')}
              description={I18n.t('js.submissionListWithdrawalPage.refundDescription')}
            >
              <SelectField
                value={refundMethod}
                options={refundMethodOptions}
                error={errors.refund_method}
                onChange={(value) => { setRefundMethod(value); setErrors((previous) => ({ ...previous, refund_method: false })) }}
              />
            </FormSection>

            {
              bankDetailsRequired &&
                <FormSection
                  title={I18n.t('js.submissionListWithdrawalPage.bankDetailsTitle')}
                  description={I18n.t('js.submissionListWithdrawalPage.bankDetailsDescription')}
                >
                  <div className="slx-form__fields">
                    <TextField
                      value={accountHolder}
                      label={I18n.t('js.submissionListWithdrawalPage.accountHolderPlaceholder')}
                      error={errors.account_holder}
                      onChange={(value) => { setAccountHolder(value); setErrors((previous) => ({ ...previous, account_holder: false })) }}
                    />

                    <div className="slx-form__row">
                      <TextField
                        value={iban}
                        label={I18n.t('js.submissionListWithdrawalPage.ibanPlaceholder')}
                        error={errors.iban}
                        errorMessage={I18n.t('js.submissionListWithdrawalPage.invalidIban')}
                        onChange={(value) => { setIban(value); setErrors((previous) => ({ ...previous, iban: false })) }}
                      />
                      <TextField
                        value={swift}
                        label={I18n.t('js.submissionListWithdrawalPage.swiftPlaceholder')}
                        error={errors.swift}
                        errorMessage={I18n.t('js.submissionListWithdrawalPage.invalidSwift')}
                        onChange={(value) => { setSwift(value); setErrors((previous) => ({ ...previous, swift: false })) }}
                      />
                    </div>
                  </div>
                </FormSection>
            }

            <FormSection title={I18n.t('js.submissionListWithdrawalPage.yourDataTitle')}>
              {
                cardsReceived ?
                  <div className="slx-form__fields">
                    <ReadOnlyField
                      label={I18n.t('js.submissionListWithdrawalPage.orderNumberLabel')}
                      value={submissionList.number}
                    />

                    <div className="slx-form__row">
                      <ReadOnlyAddressField field="first_name" address={address} />
                      <ReadOnlyAddressField field="last_name" address={address} />
                    </div>

                    <ReadOnlyAddressField field="co" address={address} />

                    <div className="slx-form__row">
                      {/* l'indirizzo porta l'id del paese: al cliente va mostrato il nome */}
                      <ReadOnlyField
                        label={I18n.t('js.submissionListWithdrawalPage.addressFields.country_id')}
                        value={countries[address.country_id]?.name}
                      />
                      <ReadOnlyAddressField field="province" address={address} />
                    </div>

                    <div className="slx-form__row">
                      <ReadOnlyAddressField field="city" address={address} />
                      <ReadOnlyAddressField field="cap" address={address} />
                    </div>

                    <div className="slx-form__row">
                      <ReadOnlyAddressField field="street_square" address={address} />
                      <ReadOnlyAddressField field="number" address={address} />
                    </div>

                    <div className="slx-form__row">
                      <ReadOnlyAddressField field="indoor" address={address} />
                      <ReadOnlyAddressField field="phone_number" address={address} />
                    </div>

                    <ReadOnlyAddressField field="email" address={address} />
                  </div>
                :
                  <div className="slx-form__fields">
                    <ReadOnlyField
                      label={I18n.t('js.submissionListWithdrawalPage.orderNumberLabel')}
                      value={submissionList.number}
                    />

                    <div className="slx-form__row">
                      <ReadOnlyField
                        label={I18n.t('js.submissionListWithdrawalPage.addressFields.first_name')}
                        value={orderOwner.first_name}
                      />
                      <ReadOnlyField
                        label={I18n.t('js.submissionListWithdrawalPage.addressFields.last_name')}
                        value={orderOwner.last_name}
                      />
                    </div>

                    <ReadOnlyField
                      label={I18n.t('js.submissionListWithdrawalPage.addressFields.email')}
                      value={orderOwner.email}
                    />
                  </div>
              }
            </FormSection>
          </div>

          <div className="slx-form__footer">
            <a href={`/submission_lists/${submissionList.id}`} className="slx-form__back">
              <i className="fas fa-chevron-left" />
              {I18n.t('js.submissionListWithdrawalPage.back')}
            </a>

            <button type="submit" className="slx-pill slx-pill--flat">
              {I18n.t('js.submissionListWithdrawalPage.confirm')}
            </button>
          </div>
        </div>
      </form>

      <SubmissionListWithdrawalCallbackModal
        show={showCallbackModal}
        submissionListId={submissionList.id}
        timeSlots={callbackTimeSlots}
        nonWorkingDays={callbackNonWorkingDays}
        onClose={() => setShowCallbackModal(false)}
        onDone={setCallbackRequest}
      />
    </div>
  );
};

export default SubmissionListWithdrawalForm;
